import React, { useEffect, useState } from "react";
import { Box, Typography, TextField, Button, Select, MenuItem, FormControl, Autocomplete, Popper, Grid2 } from "@mui/material";
import { darken, styled } from "@mui/system";
import ChildCareIcon from "@mui/icons-material/ChildCare"; // Children icon
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import WcIcon from '@mui/icons-material/Wc';
import axios from "axios";
import HotelList from './HotelList'
import { useBooking } from "./BookingContext";

const CustomPopper = styled(Popper)({
    "& .MuiAutocomplete-paper": {
        backgroundColor: "#fff",
        borderRadius: "8px",
        boxShadow: "0px 4px 10px rgba(0,0,0,0.2)",
        border: "1px solid #ddd",
    },
    "& .MuiAutocomplete-listbox": {
        maxHeight: "200px",
        overflowY: "auto",
        scrollbarWidth: "thin",
        scrollbarColor: "violet transparent",
    },
    "& .MuiAutocomplete-listbox::-webkit-scrollbar": {
        width: "6px",
    },
    "& .MuiAutocomplete-listbox::-webkit-scrollbar-thumb": {
        backgroundColor: "violet",
        borderRadius: "4px",
    },
});

const StyledListbox = styled("ul")({
    "& li": {
        padding: "8px 12px",
        transition: "background-color 0.2s ease",
        "&:hover": {
            backgroundColor: "rgba(138, 43, 226, 0.1)", // Light violet on hover
        },
    },
});

const BookingForm = ({ cities, handleBookNow }) => {
    console.log(cities);
    const { bookingData, setBookingData } = useBooking(); // Use context

    const [selectedCity, setSelectedCity] = useState(bookingData.selectedCity);
    const [checkInDate, setCheckInDate] = useState(bookingData.checkInDate);
    const [checkOutDate, setCheckOutDate] = useState(bookingData.checkOutDate);
    const [rooms, setRooms] = useState(bookingData.rooms);
    const [adults, setAdults] = useState(bookingData.adults);
    const adultOptions = Array.from({ length: 8 }, (_, i) => i + 1); // 1 to 10
    const roomOptions = Array.from({ length: 6 }, (_, i) => i + 1); // 1 to 10

    const [children, setChildren] = useState(bookingData.children);
    const [hotels, setHotels] = useState(null);
    const handleCityChange = (event, newValue) => {
        if (newValue) {
            setSelectedCity(newValue.Code);
            console.log("Selected City Code:", newValue.Code);
        } else {
            setSelectedCity(null); // optional: reset city when cleared
        }
    };

    useEffect(() => {
        // Sync state with context when form changes
        setBookingData({ selectedCity, checkInDate, checkOutDate, rooms, adults, children });
    }, [selectedCity, checkInDate, checkOutDate, rooms, adults, children, setBookingData]);

    const handleBookNowClick = async () => { // Add async keyword
        const formValues = {
            city: selectedCity ? selectedCity.Name : null,
            checkInDate,
            checkOutDate,
            rooms,
            adults,
            children,
        };
        console.log("Form Values:", formValues);


        const jwtToken = sessionStorage.getItem("jwtToken");
        if (!jwtToken) {
            alert("User is not authenticated.");
            return;
        }

        if (!selectedCity || !checkInDate || !checkOutDate) {
            alert("Please select a city and dates.");
            return;
        }

        const requestData = {
            CityCode: selectedCity,
            CheckIn: checkInDate,
            CheckOut: checkOutDate,
            GuestNationality: "IN",
            PaxRooms: [{
                adults,
                children,
                childrenAges: children > 0 ? Array(children).fill(5) : null // Dynamically setting children ages
            }],
            ResponseTime: 23.0,
            IsDetailedResponse: true,
            Filters: {
                refundable: false,
                noOfRooms: rooms,
                mealType: 0,
                orderBy: 0,
                starRating: 0,
                hotelName: null
            },
            Page: 1,
            PageSize: 50
        };

        try {
            const response = await axios.post(
                "https://api.ipaisa.co.in/myapp/hotels/search",
                requestData,
                { headers: { "Content-Type": "application/json", Authorization: `Bearer ${jwtToken}` } }
            );
            console.log("API Response:", response.data);
            alert("Hotels fetched successfully!");
            setHotels(response.data); // Set API response in state
        } catch (error) {
            console.error("Error fetching hotels:", error);
            alert("Failed to fetch hotels.");
        }
    };


    return (
        <>
            <Box
                sx={{
                    borderRadius: "80px",
                    padding: { xs: 2, md: 0 },
                    backgroundColor: "#e1f2f7", // Background color for the container
                    textAlign: "center",
                    maxWidth: "1200px", // Limit max width for larger screens
                    margin: "0 auto", // Center the form
                }}
            >
                <Grid2 sx={{ padding: '2rem' }}>
                    {/* Title */}
                    <Typography
                        variant="h5"
                        sx={{
                            fontWeight: "bold",
                            mb: 2,
                        }}
                    >
                        Book a Room
                    </Typography>

                    {/* Subtitle */}
                    <Typography
                        variant="body1"
                        sx={{
                            color: "text.secondary",
                            mb: 3,
                        }}
                    >
                        Discover the perfect space for you!
                    </Typography>

                    {/* All Fields in a Single Row */}
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: { xs: "column", sm: "row" }, // Column on small screens, row on larger screens
                            gap: "16px",
                            alignItems: "center",
                            justifyContent: "center",
                            mb: 2,
                        }}
                    >
                        {/* Select City */}
                        <Box sx={{ width: { xs: "100%", sm: "auto" } }}>
                            <Typography variant="body2" sx={{ textAlign: "left", mb: 0.5, fontWeight: "bold" }}>
                                Select City
                            </Typography>
                            <Autocomplete
                                options={cities}
                                getOptionLabel={(option) => option.Name}
                                onChange={handleCityChange}
                                PopperComponent={CustomPopper}
                                ListboxProps={{ component: StyledListbox }}
                                renderInput={(params) => <TextField {...params} label="City" variant="outlined" />}
                                sx={{
                                    minWidth: 120,
                                    maxWidth: 200,
                                    backgroundColor: "white",
                                    borderRadius: "8px",
                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: "8px",
                                    },
                                }}
                            />
                        </Box>

                        {/* Dates Box */}
                        <Box sx={{ width: { xs: "100%", sm: "auto" } }}>
                            <Typography
                                variant="body2"
                                sx={{
                                    textAlign: "left",
                                    mb: 0.5,
                                    fontWeight: "bold",
                                }}
                            >
                                Dates (Check-In & Check-Out)
                            </Typography>
                            <Box
                                sx={{
                                    display: "flex",
                                    gap: "16px",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    backgroundColor: "white",
                                    borderRadius: "8px",
                                    padding: "0.8rem",
                                }}
                            >
                                {/* Check-In Date */}
                                <Box>
                                    <TextField
                                        type="date"
                                        value={checkInDate}
                                        onChange={(e) => setCheckInDate(e.target.value)}

                                        InputLabelProps={{ shrink: true }}
                                        placeholder="Check-In"
                                        sx={{
                                            backgroundColor: "white",
                                            borderRadius: "8px", // Manual borderRadius
                                            "& .MuiOutlinedInput-root": {
                                                borderRadius: "8px", // Manual borderRadius
                                            },
                                            "& .MuiOutlinedInput-input::placeholder": {
                                                color: "text.secondary",
                                                opacity: 1,
                                            },
                                        }}
                                    />
                                </Box>

                                {/* Check-Out Date */}
                                <Box>
                                    <TextField
                                        type="date"
                                        value={checkOutDate}
                                        onChange={(e) => setCheckOutDate(e.target.value)}

                                        InputLabelProps={{ shrink: true }}
                                        placeholder="Check-Out"
                                        sx={{
                                            backgroundColor: "white",
                                            borderRadius: "8px", // Manual borderRadius
                                            "& .MuiOutlinedInput-root": {
                                                borderRadius: "8px", // Manual borderRadius
                                            },
                                            "& .MuiOutlinedInput-input::placeholder": {
                                                color: "text.secondary",
                                                opacity: 1,
                                            },
                                        }}
                                    />
                                </Box>
                            </Box>
                        </Box>

                        {/* Rooms, Adults, and Children Box */}
                        <Box sx={{ width: { xs: "100%", sm: "auto" } }}>
                            <Typography
                                variant="body2"
                                sx={{
                                    textAlign: "left",
                                    mb: 0.5,
                                    fontWeight: "bold",
                                }}
                            >
                                Rooms & Guests
                            </Typography>
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: { xs: "column", sm: "row" }, // Stack vertically on small screens
                                    gap: "16px",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    backgroundColor: "white",
                                    borderRadius: "8px",
                                    padding: "0.8rem",
                                }}
                            >
                                {/* Rooms Dropdown */}
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "8px",
                                        width: { xs: "100%", sm: "auto" },
                                    }}
                                >
                                    <MeetingRoomIcon fontSize="small" />
                                    <Typography variant="body2">Rooms</Typography>
                                    <FormControl
                                        sx={{
                                            minWidth: 80,
                                            backgroundColor: "white",
                                            borderRadius: "8px",
                                            "& .MuiOutlinedInput-root": {
                                                borderRadius: "8px",
                                            },
                                        }}
                                    >
                                        <Select
                                            value={rooms}
                                            onChange={(e) => setRooms(e.target.value)}
                                        >
                                            {[1, 2, 3, 4, 5, 6].map((num) => (
                                                <MenuItem key={num} value={num}>
                                                    {num}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Box>

                                {/* Adults Dropdown */}
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "8px",
                                        width: { xs: "100%", sm: "auto" },
                                    }}
                                >
                                    <WcIcon fontSize="small" />
                                    <Typography variant="body2">Adults</Typography>
                                    <FormControl
                                        sx={{
                                            minWidth: 80,
                                            backgroundColor: "white",
                                            borderRadius: "8px",
                                            "& .MuiOutlinedInput-root": {
                                                borderRadius: "8px",
                                            },
                                        }}
                                    >
                                        <Select value={adults} onChange={(e) => setAdults(e.target.value)}>
                                            {adultOptions.map((num) => (
                                                <MenuItem key={num} value={num}>
                                                    {num}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Box>

                                {/* Children Dropdown */}
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "8px",
                                        width: { xs: "100%", sm: "auto" }, // Full width on small screens
                                    }}
                                >
                                    <ChildCareIcon fontSize="small" />
                                    <Typography variant="body2">Children</Typography>
                                    <FormControl
                                        sx={{
                                            minWidth: 80,
                                            backgroundColor: "white",
                                            borderRadius: "8px", // Manual borderRadius
                                            "& .MuiOutlinedInput-root": {
                                                borderRadius: "8px", // Manual borderRadius
                                            },
                                        }}
                                    >
                                        <Select value={children} onChange={(e) => setChildren(e.target.value)}>
                                            <MenuItem value="0">1</MenuItem>
                                            <MenuItem value="1">2</MenuItem>
                                            <MenuItem value="2">3</MenuItem>
                                            <MenuItem value="2">4</MenuItem>

                                        </Select>
                                    </FormControl>
                                </Box>
                            </Box>
                        </Box>

                    </Box>

                    {/* Buttons in a Single Row */}
                    <Box
                        sx={{
                            display: "flex",
                            gap: "16px",
                            justifyContent: "center",
                            flexDirection: { xs: "column", sm: "row" }, // Column on small screens, row on larger screens
                        }}
                    >
                        {/* Check Availability Button */}
                        <Button
                            sx={{
                                color: 'white',
                                backgroundColor: "#65AEF2",
                                borderRadius: "8px", // Manual borderRadius
                                "&:hover": {
                                    backgroundColor: darken("#65AEF2", 0.5), // Darken on hover
                                },
                            }}
                        >
                            Check Available
                        </Button>

                        {/* Book Now Button */}
                        <Button
                            sx={{
                                color: 'white',
                                backgroundColor: "#65AEF2",
                                borderRadius: "8px", // Manual borderRadius
                                "&:hover": {
                                    backgroundColor: darken("#65AEF2", 0.5), // Darken on hover
                                },
                            }}
                            onClick={handleBookNowClick}
                        >
                            BOOK NOW
                        </Button>
                    </Box>
                </Grid2>
            </Box>
            {hotels ? <HotelList hotels={hotels} /> : <p>Loading...</p>}
        </>

    );
};

export default BookingForm;
